import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  Button as MuiButton,
  Checkbox,
  Container,
  FormControlLabel,
  Link,
  Switch,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import { history } from "../../..";
import PageHero from "../../shared/page-hero";
import Button from "../../shared/Button";
import { StyledTableCell } from "../../shared/styled-table-cell";
import { StyledTableRow } from "../../shared/styled-table-row";
import storageAreaService from "../../../services/storageAreaService";
import { Stockpile } from "../../../interface/api/Stockpile";
import { SmallQuantityTicket } from "../../../interface/api/SmallQuantityTicket";
import { StockpileSample } from "../../../interface/api/StockpileSample";
import { formatTicketNumber } from "../../../utils/parser";
import { formatDateTime } from "../../../utils/date-helper";
import StockpileApprovalDialog from "./stockpile-approval-dialog";

const SAMPLE_COUNT = 5;

const useStyles = makeStyles({
  table: { marginTop: "0.5rem" },
  section: { marginTop: "1rem", maxWidth: 480 },
});

const isImageLab = (key?: string | null) =>
  !!key && /\.(png|jpe?g|gif|webp|bmp)$/i.test(key);

const StockpileSamplingView = () => {
  const classes = useStyles();
  const { id }: any = useParams();
  const stockpileId = Number(id);

  const [stockpile, setStockpile] = useState<Stockpile | null>(null);
  const [loads, setLoads] = useState<SmallQuantityTicket[]>([]);
  const [marked, setMarked] = useState<Set<number>>(new Set());
  const [showMarkedOnly, setShowMarkedOnly] = useState(false);
  const [editingSamples, setEditingSamples] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [sample, setSample] = useState<StockpileSample | null>(null);
  const filterInitRef = useRef(false);

  const loadData = () => {
    storageAreaService.getStockpile(stockpileId).then((response) => {
      if (response.ok) setStockpile(response.data as Stockpile);
    });
    storageAreaService.getStockpileLoads(stockpileId).then((response) => {
      if (response.ok) {
        const data = response.data as SmallQuantityTicket[];
        setLoads(data);
        const sampled = data.filter((l) => l.isSampled).map((l) => l.id);
        if (sampled.length > 0) setMarked(new Set(sampled));
      }
    });
    // Presigned URL for the single combined lab report (for inline preview).
    storageAreaService.getStockpileSample(stockpileId).then((response) => {
      if (response.ok) setSample(response.data as StockpileSample);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const sampledLoads = useMemo(
    () => loads.filter((l) => l.isSampled),
    [loads]
  );
  const samplesPersisted = sampledLoads.length === SAMPLE_COUNT;

  const status = stockpile?.status ?? "";
  const isMarkingPhase = status === "To Be Sampled"; // pick the 5 samples
  const isUploadPhase = status === "Pending"; // upload the lab report + QP sign
  const isClosed = status === "Closed";
  // Samples can be re-selected while first picking (To Be Sampled) or by
  // re-opening the selection during the upload phase (Pending) before close.
  const markingMode = isMarkingPhase || (isUploadPhase && editingSamples);
  const showSampleColumn = markingMode; // checkboxes only while selecting
  const hasLab = !!stockpile?.labResultKey;

  // Default "show samples only" to true once samples exist (Pending/Closed).
  useEffect(() => {
    if (!filterInitRef.current && status) {
      if (status === "Pending" || status === "Closed") setShowMarkedOnly(true);
      filterInitRef.current = true;
    }
  }, [status]);

  const visibleLoads = useMemo(() => {
    const list = showMarkedOnly ? loads.filter((l) => marked.has(l.id)) : loads;
    // Sampled loads always on top.
    return [...list].sort((a, b) => Number(b.isSampled) - Number(a.isSampled));
  }, [loads, marked, showMarkedOnly]);

  const toggleMark = (ticketId: number) => {
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const onSaveSamples = () => {
    if (marked.size !== SAMPLE_COUNT) {
      toast.error(`Select exactly ${SAMPLE_COUNT} loads to sample.`);
      return;
    }
    storageAreaService
      .setStockpileSamples(stockpileId, Array.from(marked))
      .then((response) => {
        if (response.ok) {
          toast.success("Samples saved");
          setEditingSamples(false);
          loadData();
        } else {
          toast.error((response.data as string) || "Failed to save samples");
        }
      });
  };

  // Re-open the sample selection during the upload phase so a mistaken
  // selection can be corrected before the stockpile is signed and closed.
  const onEditSamples = () => {
    setMarked(new Set(sampledLoads.map((l) => l.id)));
    setShowMarkedOnly(false);
    setEditingSamples(true);
  };

  const onCancelEditSamples = () => {
    setMarked(new Set(sampledLoads.map((l) => l.id)));
    setShowMarkedOnly(true);
    setEditingSamples(false);
  };

  const onUpload = (file: File | null) => {
    if (!file) return;
    storageAreaService.uploadStockpileLab(stockpileId, file).then((response) => {
      if (response.ok) {
        toast.success("Lab result uploaded");
        loadData();
      } else {
        toast.error((response.data as string) || "Upload failed");
      }
    });
  };

  const onRemoveLab = () => {
    storageAreaService.removeStockpileLab(stockpileId).then((response) => {
      if (response.ok) {
        toast.success("Lab result removed");
        loadData();
      } else {
        toast.error((response.data as string) || "Failed to remove lab result");
      }
    });
  };

  const onApprove = (sampleDate: string, signature: string) => {
    storageAreaService
      .approveStockpile(stockpileId, sampleDate, signature)
      .then((response) => {
        if (response.ok) {
          toast.success("Stockpile approved and closed");
          history.push("/storage-areas");
        } else {
          toast.error((response.data as string) || "Failed to approve");
        }
      });
  };

  return (
    <Container maxWidth="xl">
      <PageHero
        title={stockpile ? stockpile.name ?? "Stockpile" : "Stockpile"}
        subtitle={
          isMarkingPhase
            ? `Select ${SAMPLE_COUNT} loads to sample.`
            : isUploadPhase
            ? `Upload the single combined lab report for the ${SAMPLE_COUNT} sampled loads, then sign to approve.`
            : isClosed
            ? `Closed${
                stockpile?.approvalDate
                  ? ` — approved ${formatDateTime(
                      stockpile.approvalDate,
                      true
                    )}`
                  : ""
              }.`
            : `${loads.length} load(s) assigned · Status: ${
                status || "—"
              }. Once full, close it for sampling — then select ${SAMPLE_COUNT} loads to sample, upload the combined lab report, and sign to approve.`
        }
        showBackButton={true}
      />

      {(isUploadPhase || isClosed) && (
        <Box className={classes.section}>
          <Typography variant="h6" gutterBottom>
            Sample &amp; Lab Result
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {hasLab ? (
              sample?.labResultUrl && isImageLab(stockpile?.labResultKey) ? (
                <a
                  href={sample.labResultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={sample.labResultUrl}
                    alt="Lab result"
                    style={{
                      height: 60,
                      width: "auto",
                      borderRadius: 4,
                      border: "1px solid rgba(0,0,0,0.15)",
                    }}
                  />
                </a>
              ) : sample?.labResultUrl ? (
                <Link
                  href={sample.labResultUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View lab result
                </Link>
              ) : (
                <Typography variant="body2" color="primary">
                  Uploaded
                </Typography>
              )
            ) : (
              isUploadPhase && (
                <MuiButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  component="label"
                >
                  Upload Lab Result
                  <input
                    hidden
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => onUpload(e.target.files?.[0] ?? null)}
                  />
                </MuiButton>
              )
            )}
            {isUploadPhase && hasLab && (
              <MuiButton
                size="small"
                style={{ color: "#c62828" }}
                onClick={onRemoveLab}
              >
                Remove
              </MuiButton>
            )}
          </Box>
          {isUploadPhase && !hasLab && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginTop: 4 }}
            >
              Upload the lab report to enable closing.
            </Typography>
          )}
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 12, marginTop: "1rem" }}
      >
        <Typography variant="h6">
          Loads ({loads.length})
          {markingMode ? ` — marked ${marked.size} / ${SAMPLE_COUNT}` : ""}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {(isMarkingPhase || isUploadPhase || isClosed) && (
            <FormControlLabel
              control={
                <Switch
                  checked={showMarkedOnly}
                  onChange={(e) => setShowMarkedOnly(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <span style={{ whiteSpace: "nowrap" }}>
                  {markingMode ? "Show marked only" : "Show samples only"}
                </span>
              }
            />
          )}
          {markingMode && (
            <Button
              label="Select Loads"
              color="primary"
              variant="contained"
              type="button"
              onClick={onSaveSamples}
              disabled={marked.size !== SAMPLE_COUNT}
            />
          )}
          {isUploadPhase && editingSamples && (
            <Button
              label="Cancel"
              color="default"
              variant="contained"
              type="button"
              onClick={onCancelEditSamples}
            />
          )}
          {isUploadPhase && !editingSamples && (
            <>
              <Button
                label="Edit Samples"
                color="default"
                variant="contained"
                type="button"
                onClick={onEditSamples}
              />
              <Button
                label="Sign & Close"
                color="primary"
                variant="contained"
                type="button"
                onClick={() => setApprovalOpen(true)}
                disabled={!samplesPersisted || !hasLab}
              />
            </>
          )}
        </Box>
      </Box>

      <TableContainer>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {showSampleColumn && <StyledTableCell>Sample</StyledTableCell>}
              <StyledTableCell>Ticket #</StyledTableCell>
              <StyledTableCell>Contractor</StyledTableCell>
              <StyledTableCell>Job Site</StyledTableCell>
              <StyledTableCell>Haulage Company</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Truck #</StyledTableCell>
              <StyledTableCell>License Plate</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLoads.map((load) => (
              <StyledTableRow key={load.id}>
                {showSampleColumn && (
                  <StyledTableCell>
                    <Checkbox
                      color="primary"
                      checked={marked.has(load.id)}
                      onChange={() => toggleMark(load.id)}
                    />
                  </StyledTableCell>
                )}
                <StyledTableCell>
                  {formatTicketNumber(load.ticketNumber)}
                </StyledTableCell>
                <StyledTableCell>
                  {load.contractorCompanyName ?? "—"}
                </StyledTableCell>
                <StyledTableCell>{load.jobSite ?? "—"}</StyledTableCell>
                <StyledTableCell>
                  {load.haulageCompanyName ?? "—"}
                </StyledTableCell>
                <StyledTableCell>
                  {formatDateTime(load.timeReceived as any, false)}
                </StyledTableCell>
                <StyledTableCell>{load.truckNumber}</StyledTableCell>
                <StyledTableCell>{load.truckLicensePlate}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <StockpileApprovalDialog
        open={approvalOpen}
        stockpileName={stockpile?.name ?? null}
        onCancel={() => setApprovalOpen(false)}
        onApprove={(sampleDate, signature) => {
          setApprovalOpen(false);
          onApprove(sampleDate, signature);
        }}
      />
    </Container>
  );
};

export default StockpileSamplingView;
